import { NavLink, useNavigate, useParams } from "react-router";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "@/routes/customers/[customerId]/_components/customer-page";
import { CustomerPageBreadcrumbs } from "@/routes/customers/[customerId]/_components/customer-page-breadcrumbs";
import t from "@/lang/lang";
import {
  PutRequest,
  useCustomersServiceObjectPutMutation,
  useCustomersServiceObjectShowQuery,
} from "@/services/backend/customers/api/service-object-api";
import { H3 } from "@/shared/components/typography";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  RelationshipsTable,
  RelationshipsTableFilters,
} from "@/services/backend/relationships/components/relationships-table";
import { ServiceObject } from "@/services/backend/customers/types/service-object";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { CreateRelationshipDialog } from "@/services/backend/relationships/components/create-relationship-dialog";
import { ReactNode, useCallback, useState } from "react";
import { ListRequest } from "@/services/backend/relationships/api/service";
import { ServiceAddressCard } from "@/services/backend/addresses/components/service-address-card";
import { TextInput } from "@/shared/components/form/text-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import {
  fieldErrorsForPrefix,
  rtkErrIsValidationError,
} from "@/shared/lib/errors/validation-error";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteServiceObjectDialog } from "@/services/backend/customers/components/delete-service-object-dialog";

export function ServiceObjectRoute() {
  const { customerId, serviceObjectId } = useParams();
  const navigate = useNavigate();

  const { data: serviceObject, error } = useCustomersServiceObjectShowQuery({
    id: serviceObjectId!,
  });

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs
          breadcrumbs={[
            {
              to: `/customers/${customerId}/service-objects`,
              label: t("Service Objekte"),
            },
            {
              to: `/customers/${customerId}/service-objects/${serviceObjectId}`,
              label: serviceObject?.name,
            },
          ]}
        />
      </CustomerPageMainHeader>
      <CustomerPageMainContent className="gap-4">
        <PersistenceContextProvider>
          {error && <RTKQueryErrorAlert error={error} />}
          <div className="flex items-center gap-4">
            <H3>{serviceObject?.name}</H3>
            <PersistenceStateUsingContext className="ml-auto" />
            {serviceObject && (
              <DeleteServiceObjectDialog
                serviceObjectId={serviceObject.id}
                onDeleted={() =>
                  navigate(`/customers/${customerId}/service-objects`)
                }
              />
            )}
          </div>
          {serviceObject && <ServiceObjectData serviceObject={serviceObject} />}
          {serviceObject && <Relationships serviceObject={serviceObject} />}
        </PersistenceContextProvider>
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function ServiceObjectData({
  serviceObject,
}: {
  serviceObject: ServiceObject;
}) {
  const [request, setRequest] = useState<PutRequest>({ ...serviceObject });
  const mutation = useCustomersServiceObjectPutMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    ...serviceObject,
  });

  return (
    <>
      <BaseDataCard request={request} setRequest={setRequest} error={error} />
      <ServiceAddressCard
        address={request.address}
        onAddressChange={(address) => setRequest({ ...request, address })}
        fieldErrors={
          rtkErrIsValidationError(error)
            ? fieldErrorsForPrefix(error.data, "address.")
            : undefined
        }
        hideNavigationButton
      />
    </>
  );
}

function BaseDataCard({
  request,
  setRequest,
  error,
}: {
  request: PutRequest;
  setRequest: (request: PutRequest) => void;
  error: unknown;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Basisdaten")}</CardTitle>
      </CardHeader>
      <CardContent>
        <TextInput
          label={t("Name")}
          field="name"
          text={request.name}
          onTextChange={(name) => setRequest({ ...request, name })}
          errors={inputValidationErrorMessages(error, "name")}
        />
      </CardContent>
    </Card>
  );
}

function Relationships({ serviceObject }: { serviceObject: ServiceObject }) {
  const [request, setRequest] = useState<ListRequest>({
    serviceObject: {
      active: true,
      values: [serviceObject.id],
    },
    orderBy: [{ field: "updatedAt" }],
    limit: 10,
    offset: 0,
  });

  const renderRowLink = useCallback(
    (relationship: { id: string; customerId: string }, children: ReactNode) => (
      <NavLink
        to={`/customers/${relationship.customerId}/relationships/${relationship.id}`}
      >
        {children}
      </NavLink>
    ),
    [],
  );

  return (
    <Card>
      <CardHeader className="grid grid-cols-1 sm:grid-cols-[1fr_auto]">
        <div>
          <CardTitle>{t("Beziehungen")}</CardTitle>
          <CardDescription>
            {t("Diese Beziehungen bestehen für das Objekt.")}
          </CardDescription>
        </div>
        <CreateRelationshipDialog
          customerId={serviceObject.customerId}
          serviceObjectId={serviceObject.id}
        />
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          <RelationshipsTableFilters
            request={request}
            onRequestChange={setRequest}
            hideCustomerFilter
            hideServiceObjectFilter
          />
          <RelationshipsTable
            request={request}
            onRequestChange={setRequest}
            hideBorder
            renderRowLink={renderRowLink}
          />
        </div>
      </CardContent>
    </Card>
  );
}
